import React, { useContext } from 'react'
import { connect } from 'react-redux'
import HeadData from 'components/Common/HeadData'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import PaneBody from 'components/Common/Pane/PaneBody'
import Faq from 'components/Common/Faq'
import Image from 'components/Common/Image'

import {
  Root,
  TextPanelContent,
  FinePrintLinks,
  JoinPanel,
  CommonHeaderTop,
  CommonMainHeaderBox, CommonStepBox,
  CommonStepNumber,
} from './../style'
import TextButton from 'components/Luxkit/Button/TextButton'
import TextLink from 'components/Luxkit/TextLink'
import {
  alreadyLinked,
  getPartnershipPrefixUpperCase,
} from 'selectors/partnershipSelectors'
import HttpStatus from 'components/Common/HttpStatus'
import Error404Page from 'components/Pages/Error404Page'
import {
  accountAccessShowLogin,
} from 'actions/UiActions'
import { selectLoggedIn } from 'selectors/accountSelectors'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Heading from 'components/Luxkit/Typography/Heading'
import { promotionBanner } from 'analytics/promotions'
import { addGTMEvent } from 'api/googleTagManager'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { rem } from 'polished'
import styled from 'styled-components'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ModalContext from 'contexts/ModalContext'
import AccountAccessPartnershipLinkAccount from 'components/Account/AccountAccess/AccountAccessPartnershipLinkAccount'
import ModalBase from 'components/Luxkit/Modal/ModalBase'
import HeroImage from 'content/components/HeroImage'
import FormatCurrency from 'components/Common/FormatCurrency'
import Divider from 'components/Luxkit/Divider'
import ResponsiveLuxuryEscapesLogo from 'components/App/Header/Logo/logos/ResponsiveLuxuryEscapesLogo'
import Group from 'components/utils/Group'
import ResponsiveImage from 'components/Common/ResponsiveImage'

const HeroContainer = styled(HeroImage)`
  padding-inline: 0;
  min-height: ${rem(280)};

  ${mediaQueryUp.desktop} {
    min-height: ${rem(300)};
  }
`

const LEKrisFlyerLogoContainer = styled(Group)`
  height: ${rem(50)};

  ${mediaQueryUp.tablet} {
    min-width: ${rem(400)};
  }
`

const ThreeColumn = styled.div`
  display: grid;
  gap: ${rem(12)};

  ${mediaQueryUp.desktop}{
    grid-template-columns: repeat(3, 1fr);
  }
`

const HERO_IMAGE_ID = 'shutterstock_284109371_x4swxr'

const REGION_TO_AMOUNT_PER_MILE: Record<string, number> = {
  CN: 10,
  HK: 10,
  ID: 16500,
  JP: 160,
  KR: 1500,
  MY: 5,
  PH: 60,
  SG: 1.50,
  TH: 35,
  VN: 25500,
}

interface Props {
  accountAccessShowLogin: () => void;
  regionCode: string;
  linkPartnershipAccount: string;
  loggedIn?: boolean;
  alreadyLinked?: boolean;
  partnership: App.Partnership;
}

function KrisFlyerLandingPageContainer(props: Props) {
  const {
    loggedIn,
    alreadyLinked,
  } = props
  const showModal = useContext(ModalContext)

  const onClickTrack = (e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
    const target = e.currentTarget
    const data = target.dataset
    if (data.bannerid) {
      const bannerId = data.bannerid
      addGTMEvent(promotionBanner({ bannerId, hitType: 'partnership-click' }))
    }
  }

  const showJoin = (e: React.MouseEvent<HTMLButtonElement>) => {
    onClickTrack(e)
    props.accountAccessShowLogin()
  }

  const getButton = (bannerID: string) => {
    if (loggedIn && alreadyLinked) {
      return <TextButton kind="primary" data-testid={bannerID} data-bannerid={bannerID} onClick={onClickTrack} href="/">
        Browse offers
      </TextButton>
    } else if (loggedIn) {
      return <TextButton kind="primary" data-testid={bannerID} data-bannerid={bannerID} onClick={() => { showModal(<ModalBase><AccountAccessPartnershipLinkAccount /></ModalBase>) }}>
        Link your account
      </TextButton>
    } else {
      return <TextButton kind="primary" data-testid={bannerID} data-bannerid={bannerID} onClick={showJoin}>
        Join Luxury Escapes
      </TextButton>
    }
  }

  const {
    regionCode,
    partnership,
  } = props

  const regionEarningAmount: number = REGION_TO_AMOUNT_PER_MILE[regionCode]

  if (!partnership || partnership.prefix !== 'kfp' || !partnership.regionCodes.includes(regionCode)) {
    return <HttpStatus code={404}>
      <Error404Page invalidRegion />
    </HttpStatus>
  }

  return (
    <Root>
      <HeadData
        title="KrisFlyer"
        description="Earn KrisFlyer miles on eligible holidays."
      />

      <HeroContainer
        alt="KrisFlyer"
        id={HERO_IMAGE_ID}
      >
        <CommonHeaderTop>
          <LayoutContainer>
            <CommonMainHeaderBox>
              <VerticalSpacer gap={16}>
                <LEKrisFlyerLogoContainer fullWidth direction="horizontal" gap={8} horizontalAlign="center" verticalAlign="center">
                  <ResponsiveLuxuryEscapesLogo />
                  <Divider kind="primary" direction="vertical" />
                  <ResponsiveImage
                    quality="best"
                    width={100}
                    id="5a5ojgx0m83hq9zzzoda"
                    alt="KrisFlyer Logo"
                  />
                </LEKrisFlyerLogoContainer>

                <Heading variant="heading4" as="h1">
                  Earn 1 KrisFlyer mile per <FormatCurrency value={regionEarningAmount}/><br />spent on eligible holidays.
                </Heading>
              </VerticalSpacer>
            </CommonMainHeaderBox>
          </LayoutContainer>
        </CommonHeaderTop>
      </HeroContainer>
      <PaneBody size="xl-y">
        <TextPanelContent size="sm">
          <Heading variant="heading3">Luxury Escapes + KrisFlyer</Heading>
          <BodyText variant="large">We're committed to providing great deals to amazing places and now Luxury Escapes members can also earn KrisFlyer miles.</BodyText>
          {getButton('kfpAccountLink1')}
        </TextPanelContent>
      </PaneBody>
      <Pane>
        <PaneBody size="xl-y">
          <TextPanelContent>
            <Heading variant="heading2">Earn KrisFlyer miles</Heading>
            <ThreeColumn>
              <CommonStepBox>
                <CommonStepNumber>
                  <BodyText variant="medium" weight="bold">1</BodyText>
                </CommonStepNumber>
                <BodyText variant="large" weight="bold">Sign up and link</BodyText>
                <BodyText variant="medium">Join Luxury Escapes, then follow the prompts to link your KrisFlyer account and Luxury Escapes accounts.</BodyText>
              </CommonStepBox>
              <CommonStepBox>
                <CommonStepNumber>
                  <div>
                    <BodyText variant="medium" weight="bold">2</BodyText>
                  </div>
                </CommonStepNumber>
                <BodyText variant="large" weight="bold">Explore and book</BodyText>
                <BodyText variant="medium">Select your dream destination and make sure you have added your KrisFlyer ID before checking out.</BodyText>
              </CommonStepBox>
              <CommonStepBox>
                <CommonStepNumber>
                  <div>
                    <BodyText variant="medium" weight="bold">3</BodyText>
                  </div>
                </CommonStepNumber>
                <BodyText variant="large" weight="bold">Earn KrisFlyer miles</BodyText>
                <BodyText variant="medium">Earn 1 KrisFlyer mile per <FormatCurrency value={regionEarningAmount}/> spent on eligible holidays.</BodyText>
              </CommonStepBox>
            </ThreeColumn>
          </TextPanelContent>
        </PaneBody>
      </Pane>

      <JoinPanel>
        <TextPanelContent>
          <Image
            quality="best"
            width={172}
            id="5a5ojgx0m83hq9zzzoda"
            alt="KrisFlyer Logo"
          />
          <BodyText variant="large" weight="bold">Not a KrisFlyer member?</BodyText>
          <BodyText variant="large">Join now for free</BodyText>
          <TextButton
            kind="primary"
            onClick={onClickTrack}
            data-bannerid="kfpJoin"
            data-testid="singapore-airlines-destination"
            href="https://www.singaporeair.com/en_UK/ppsclub-krisflyer/registration-form/"
            target="_blank"
          >
            Join KrisFlyer
          </TextButton>
          <FinePrintLinks>
            <TextLink to="/terms-and-conditions#krisFlyer" data-bannerid="kfpTnC" onClick={onClickTrack}>
              Terms & Conditions
            </TextLink>
          </FinePrintLinks>
        </TextPanelContent>
      </JoinPanel>
      <Pane>
        <PaneBody size="xl-y">
          <LayoutContainer>
            <VerticalSpacer gap={40}>
              <Heading variant="heading3" align="center" as="h3">Frequently Asked Questions</Heading>
              <BodyText variant="large" align="start">
                <Faq maxToShow={10} slug="kfp-faq"/>
              </BodyText>
            </VerticalSpacer>
          </LayoutContainer>
        </PaneBody>
      </Pane>
    </Root>
  )
}

const mapStateToProps = (state: App.State) => ({
  loggedIn: selectLoggedIn(state),
  alreadyLinked: alreadyLinked(state),
  regionCode: state.geo.currentRegionCode,
  partnership: state.geo.partnership,
  linkPartnershipAccount: `${getPartnershipPrefixUpperCase(state)}Link`,
})

const mapDispatchToProps = {
  accountAccessShowLogin,
}

export default connect(mapStateToProps, mapDispatchToProps)(KrisFlyerLandingPageContainer)
